import { queryOptions } from "@tanstack/solid-query";
import { axiosClient } from "~/js/network/axiosClient";
import {
	extractIntervalFromApiLabel,
	type INewPricingResponse,
	type IPlan,
	type IProductList,
	tariffToIconFeatures
} from "~/solidJs/entities/PremiumPopUp/helpers/IProducts";
import type { Testimonial } from "~/solidJs/entities/PremiumPopUp/ui/Testimonials";
import type { GroupedQa } from "~/solidJs/pages/PricingPage/model";
import { fetchAirTablePagination } from "~/solidJs/shared/api/fetchAirTablePagination";
import type { UnsubscribeFormStep } from "~/solidJs/widgets/UnsubscribeModalWidget/model";
import type {
	TariffBenefits,
	TariffBenefitsAirtable,
	UnsubscribeQuestionAirtableItem,
	UnsubscribeQuestionItem
} from "~/solidJs/widgets/UnsubscribeQuestionsWidget/UnsubscribeQuestionsWidget";
import type { PromoCodesVerifyResponse } from "../../entities/PremiumPopUp/helpers/IProducts";
import type {
	AccordionsTables,
	GroupedTable
} from "../../entities/PricingFeaturesTableEntity/types";
import type { FeedBackListItem } from "../../entities/PricingFeedBackEntity/types";
import type { UserSubscriptionPlanEnum } from "../helpers/types";

// Types for Q&A API response
export interface QaApiItem {
	question: string;
	answer: string;
}

export interface QaApiResponse {
	qa: Record<string, QaApiItem[]>;
}

export class ProductsApi {
	static getProductListOptions() {
		return queryOptions({ queryKey: ["productList"], queryFn: ProductsApi.getProductList });
	}

	// New methods for the refactored pricing structure
	static getNewProductListOptions() {
		return queryOptions({
			queryKey: ["newProductList"],
			queryFn: ProductsApi.getNewProductList
		});
	}

	// Q&A methods
	static getQaDataOptions() {
		return queryOptions({
			queryKey: ["qaData"],
			queryFn: ProductsApi.getQaData
		});
	}
	static async getProductList(): Promise<{
		productList: IProductList;
		priceAccum: { value: string; label: string }[];
		maxFeatureLength: number;
	}> {
		const { data } = await axiosClient.axiosClient.get<IProductList>(
			"api/v2/payments/plans_v2"
		);
		const pricesForEnum: { value: string; label: string }[] = [];
		const firstProductListRange = data[Object.keys(data)[0]].products;
		const maxFeatureLength = firstProductListRange.reduce(
			(acc, product) => Math.max(acc, product.featuresV2.length),
			0
		);
		for (const key in data) {
			const productList = data[key];
			productList.products.sort(
				(productA, productB) =>
					Number(productA.sorting_order) - Number(productB.sorting_order)
			);
			for (const product of productList.products) {
				if (product.nameId in tariffToIconFeatures) {
					product.iconsFeatures = tariffToIconFeatures[product.nameId];
				}
			}

			pricesForEnum.push({ value: key, label: data[key].front_label });
		}

		return {
			productList: data,
			maxFeatureLength,
			priceAccum: pricesForEnum
		};
	}
	static async getLegacyProductList(): Promise<{
		productList: IProductList;
		priceAccum: { value: string; label: string }[];
		maxFeatureLength: number;
	}> {
		const { data } = await axiosClient.axiosClient.get<IProductList & { base_coasts: any }>(
			"api/v2/payments/plans",
			{
				params: { is_old: true }
			}
		);
		delete data.base_coasts;
		const pricesForEnum: { value: string; label: string }[] = [];
		const firstProductListRange = data[Object.keys(data)[0]].products;
		const maxFeatureLength = firstProductListRange.reduce(
			(acc, product) => Math.max(acc, product.featuresV2.length),
			0
		);
		for (const key in data) {
			const productList = data[key];
			productList.products.sort(
				(productA, productB) =>
					Number(productA.sorting_order) - Number(productB.sorting_order)
			);
			for (const product of productList.products) {
				if (product.nameId in tariffToIconFeatures) {
					product.iconsFeatures = tariffToIconFeatures[product.nameId];
				}
			}

			pricesForEnum.push({ value: key, label: data[key].front_label });
		}

		return {
			productList: data,
			maxFeatureLength,
			priceAccum: pricesForEnum
		};
	}
	static async getPormocodePrices(code: string) {
		const { data } = await axiosClient.axiosClient.post<PromoCodesVerifyResponse>(
			"/api/v2/payments/promo/check",
			{},
			{ params: { code } }
		);
		return data;
	}

	// New API methods for refactored pricing structure
	static async getNewProductList(): Promise<{
		plans: IPlan[];
		priceAccum: { value: string; label: string }[];
		baseCostGeneration: INewPricingResponse["base_cost_generation"];
		availableIntervals: string[];
	}> {
		const { data } = await axiosClient.axiosClient.get<INewPricingResponse>(
			"api/v2/payments/plans_v2"
		);

		// Sort plans by sorting_order
		data.plans.sort((a, b) => a.sorting_order - b.sorting_order);

		// Add icon features to plans if they exist
		for (const plan of data.plans) {
			if (plan.name in tariffToIconFeatures) {
				plan.iconsFeatures =
					tariffToIconFeatures[plan.name as keyof typeof tariffToIconFeatures];
			}
			// Map features to featuresV2 for backward compatibility
			plan.featuresV2 = plan.features;
		}

		// Extract available intervals from costs
		const intervals = new Set<string>();
		data.plans.forEach((plan) => {
			plan.costs.forEach((cost) => {
				const extractedInterval = extractIntervalFromApiLabel(cost.api_label);
				intervals.add(extractedInterval);
			});
		});

		// Create price accumulator for intervals
		const priceAccum: { value: string; label: string }[] = Array.from(intervals).map(
			(interval) => ({
				value: interval,
				label:
					interval === "1mo"
						? "Monthly"
						: interval === "6mo"
							? "6 Months"
							: interval === "12mo"
								? "Annual"
								: interval.charAt(0).toUpperCase() + interval.slice(1)
			})
		);

		return {
			plans: data.plans,
			priceAccum,
			baseCostGeneration: data.base_cost_generation,
			availableIntervals: Array.from(intervals)
		};
	}

	static async getQaData(): Promise<GroupedQa> {
		const { data } = await axiosClient.axiosClient.get<QaApiResponse>(
			"api/v2/payments/questions"
		);

		// Transform API response to GroupedQa format
		const questions = Object.entries(data.qa).map(([name, items]) => ({
			heading: name,
			questions: items.map((item) => ({
				id: item.question,
				question: item.question,
				answer: item.answer
			}))
		}));

		// For now, group all questions under a single heading
		// This can be enhanced later to support multiple groups
		return {
			items: questions
		};
	}
	static key_getTariffBenifits = "key_getTariffBenifits";
	static async getTariffBenifits(
		group: UserSubscriptionPlanEnum
	): Promise<TariffBenefits | null> {
		const { records } = await fetchAirTablePagination<TariffBenefitsAirtable>({
			baseId: "app3F09VAe9P8yfA5",
			tableName: "unsubscribeToBenefits",
			view: "Main",
			axiosInstance: axiosClient.axiosClient,
			formula: `userGroup="${group}"`
		});
		if (records.length === 0) return null;
		const item = {} as TariffBenefits;
		try {
			item.userGroup = records[0].fields.userGroup;
			item.Title = records[0].fields.Title;
			item.Features = JSON.parse(records[0].fields.Features);
		} catch {
			return null;
		}

		return item;
	}

	static key_getTestimonials = "key_getTestimonials";
	static async getTestimonials(): Promise<Testimonial[]> {
		try {
			const records = await fetchAirTablePagination<{ Name: string; imageCdn: string }>({
				baseId: "app3F09VAe9P8yfA5",
				tableName: "testimonials",
				view: "Main",
				axiosInstance: axiosClient.axiosClient
			});
			const res: Testimonial[] = [];
			for (const rec of records.records) {
				res.push({
					name: rec.fields.Name,
					src: rec.fields.imageCdn
				});
			}
			return res;
		} catch (e) {
			console.log(e);
			return [];
		}
	}
	static getPricingTableDataGroupedOptions() {
		return queryOptions({
			queryKey: ["getPricingTableDataGroupedOptions"],
			queryFn: ProductsApi.getPricingTableDataGrouped
		});
	}
	static async getPricingTableDataGrouped(): Promise<GroupedTable[] | undefined> {
		try {
			const records = await fetchAirTablePagination<{
				Data: string;
				accordionData: string;
				name: string;
				TableDataV2: string;
			}>({
				baseId: "app3F09VAe9P8yfA5",
				tableName: "pricingFeatures",
				view: "Main",
				axiosInstance: axiosClient.axiosClient,
				fieldsToSelect: ["TableDataV2"]
			});
			const dataJson = records.records[0].fields.TableDataV2;
			return JSON.parse(dataJson);
		} catch (e) {
			console.log(e);
		}
	}
	static async getPricingTableData(): Promise<AccordionsTables | undefined> {
		try {
			const records = await fetchAirTablePagination<{
				Data: string;
				accordionData: string;
				name: string;
			}>({
				baseId: "app3F09VAe9P8yfA5",
				tableName: "pricingFeatures",
				view: "Main",
				axiosInstance: axiosClient.axiosClient,
				fieldsToSelect: ["accordionData"]
			});
			const dataJson = records.records[0].fields.accordionData;
			return JSON.parse(dataJson);
		} catch (e) {
			console.log(e);
		}
	}
	static async getPricingFeedBackData(): Promise<FeedBackListItem[] | undefined> {
		try {
			const records = await fetchAirTablePagination<{
				name: string;
				position: string;
				feedBack: string;
				userPickCdn: string;
			}>({
				baseId: "app3F09VAe9P8yfA5",
				tableName: "PricingReviews",
				view: "Main",
				axiosInstance: axiosClient.axiosClient,
				fieldsToSelect: ["name", "position", "feedBack", "userPickCdn"]
			});
			const feedBackList: FeedBackListItem[] = [];
			for (const record of records.records) {
				const feedBackItem = {} as FeedBackListItem;
				feedBackItem.feedBack = record.fields.feedBack;
				feedBackItem.name = record.fields.name;
				feedBackItem.position = record.fields.position;
				feedBackItem.userPick = record.fields.userPickCdn;

				feedBackList.push(feedBackItem);
			}
			return feedBackList;
		} catch (e) {
			console.log(e);
		}
	}
	static getUnsubscribeQuestionsKey = "_getUnsubscribeQuestionsKey" as const;
	static async getUnsubscribeQuestions(params?: {
		is_companies?: boolean;
	}): Promise<UnsubscribeFormStep[]> {
		const data = await axiosClient.axiosClient.get<{ questions: UnsubscribeFormStep[] }>(
			"api/v2/payments/cancellation_questions",
			{ params }
		);

		return data.data.questions;
	}
	static async postUnsubscribeQuestions(data: { id: string; value: string }[]) {
		await axiosClient.axiosClient.post<UnsubscribeFormStep[]>(
			"api/v2/payments/cancellation_answers",
			{
				answers: data.map((item) => ({
					question_id: item.id,
					answer: item.value
				}))
			}
		);
	}
	static async postUnsubscribeQuestionsResults(): Promise<UnsubscribeQuestionItem[] | undefined> {
		try {
			const records = await fetchAirTablePagination<UnsubscribeQuestionAirtableItem>({
				baseId: "app3F09VAe9P8yfA5",
				tableName: "unsubscribeQuestionList",
				view: "Main",
				axiosInstance: axiosClient.axiosClient,
				fieldsToSelect: ["variantName", "variantNameId", "fields"]
			});
			const questionList: UnsubscribeQuestionItem[] = [];
			for (const record of records.records) {
				const feedBackItem = {} as UnsubscribeQuestionItem;
				feedBackItem.variantName = record.fields.variantName;
				feedBackItem.variantNameId = record.fields.variantNameId;
				try {
					feedBackItem.fields = JSON.parse(record.fields.fields);
				} catch (e) {
					console.log(e);
				}

				questionList.push(feedBackItem);
			}
			return questionList;
		} catch (e) {
			console.log(e);
		}
	}
}
